/**
 * Image Browsing Tracker
 *
 * Detects when a shopper is extensively browsing product images
 * by tracking image gallery interactions within a time window.
 *
 * Fires when: 6+ image views within 60 seconds
 */

import {
  SHOPPER_INTENT_EVENTS,
  type ImageBrowsingEventData,
} from "@redotech/redo-model/events/intent/shopper-intent";
import { fireIntentEvent } from "./types";

interface ImageView {
  timestamp: number;
  imageIndex: number;
}

const STORAGE_KEY = "redo-image-browsing-state";
const IMAGE_VIEW_THRESHOLD = 6;
const IMAGE_VIEW_WINDOW_MS = 60_000;
const DEBOUNCE_MS = 150;

interface ImageBrowsingState {
  imageViews: ImageView[];
  hasFired: boolean;
}

function loadState(): ImageBrowsingState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore storage errors
  }
  return { imageViews: [], hasFired: false };
}

function saveState(state: ImageBrowsingState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

function pruneOldEntries(entries: ImageView[]): ImageView[] {
  const cutoff = Date.now() - IMAGE_VIEW_WINDOW_MS;
  return entries.filter((e) => e.timestamp > cutoff);
}

function detectCurrentProductId(): string | null {
  const shopifyProduct = (globalThis as any).ShopifyAnalytics?.meta?.product;
  if (shopifyProduct?.id) {
    return String(shopifyProduct.id);
  }

  const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (match) {
    return match[1] ?? null;
  }

  return null;
}

const GALLERY_SELECTORS = [
  ".product-gallery",
  ".product__media-list",
  "[data-product-media-container]",
  ".product-single__photos",
  ".product-images",
  ".swiper-container",
  ".slick-slider",
];

const ACTIVE_SLIDE_SELECTORS =
  ".is-active, .slick-active, .swiper-slide-active, [aria-current='true']";

const SLIDE_SELECTORS =
  ".product__media-item, .swiper-slide, .slick-slide:not(.slick-cloned)";

export interface ImageBrowsingTracker {
  cleanup: () => void;
}

export function setupImageBrowsingTracker(): ImageBrowsingTracker {
  const state = loadState();

  // Skip setup entirely if already fired
  if (state.hasFired) {
    return { cleanup: () => {} };
  }

  state.imageViews = pruneOldEntries(state.imageViews);
  saveState(state);

  const observers: MutationObserver[] = [];
  let currentImageIndex = 0;
  let handleGalleryClick: (() => void) | undefined;
  let galleryContainer: Element | null = null;
  let debounceTimeout: ReturnType<typeof setTimeout> | null = null;

  const checkAndFireEvent = (): void => {
    if (state.hasFired) {
      return;
    }

    const recentViews = pruneOldEntries(state.imageViews);

    if (recentViews.length >= IMAGE_VIEW_THRESHOLD) {
      state.hasFired = true;
      saveState(state);

      const eventData: ImageBrowsingEventData = {
        viewCount: recentViews.length,
        windowMs: IMAGE_VIEW_WINDOW_MS,
        productId: detectCurrentProductId(),
      };

      fireIntentEvent({
        type: SHOPPER_INTENT_EVENTS.IMAGE_BROWSING,
        data: eventData,
      });
    }
  };

  const recordImageView = (imageIndex: number): void => {
    state.imageViews.push({ timestamp: Date.now(), imageIndex });

    state.imageViews = pruneOldEntries(state.imageViews);
    saveState(state);
    checkAndFireEvent();
  };

  // Find gallery container
  galleryContainer =
    GALLERY_SELECTORS.map((s) => document.querySelector(s)).find(Boolean) ??
    null;

  if (galleryContainer) {
    const detectCurrentImage = (): number => {
      const activeSlide =
        galleryContainer?.querySelector(ACTIVE_SLIDE_SELECTORS) ??
        galleryContainer?.querySelector(".product__media-item:not([hidden])");

      if (activeSlide) {
        const slides = Array.from(
          galleryContainer?.querySelectorAll(SLIDE_SELECTORS) ?? [],
        );
        return slides.indexOf(activeSlide);
      }

      return 0;
    };

    currentImageIndex = detectCurrentImage();

    const handleImageChange = (): void => {
      const newIndex = detectCurrentImage();
      if (newIndex !== currentImageIndex && newIndex >= 0) {
        currentImageIndex = newIndex;
        recordImageView(newIndex);
      }
    };

    // Debounced handler for MutationObserver
    const debouncedHandleImageChange = (): void => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      debounceTimeout = setTimeout(handleImageChange, DEBOUNCE_MS);
    };

    // Observe gallery changes via MutationObserver (debounced)
    const galleryObserver = new MutationObserver(debouncedHandleImageChange);

    galleryObserver.observe(galleryContainer, {
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "aria-current", "hidden", "style"],
    });

    observers.push(galleryObserver);

    // Also listen for click events on thumbnails
    handleGalleryClick = (): void => {
      setTimeout(handleImageChange, 100);
    };

    galleryContainer.addEventListener("click", handleGalleryClick);
  }

  return {
    cleanup: () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      for (const o of observers) o.disconnect();
      if (galleryContainer && handleGalleryClick) {
        galleryContainer.removeEventListener("click", handleGalleryClick);
      }
    },
  };
}
